import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRightIcon, CheckCircleIcon, PulseIcon } from '@phosphor-icons/react';
import { SEVERITIES, type MonitorHistoryRange } from '@meshalert/contract';
import { api } from '../lib/api.ts';
import {
  HISTORY_RANGE_LABEL,
  SEVERITY_LABEL,
  historyStartLabel,
  percent,
  relativeTime,
  uptimePercent,
  viewerTimeZone,
} from '../lib/format.ts';
import { HistoryRangeTabs, MonitorHistoryChart, StripLegend, UptimeStrip } from '../components/MonitorHistoryChart.tsx';
import { AlertTimeline } from '../components/AlertTimeline.tsx';
import { PageHeader } from '../components/Layout.tsx';
import {
  EmptyState,
  ErrorState,
  ListSkeleton,
  Panel,
  SeverityChip,
  Skeleton,
  StatTile,
  cx,
  severityRuleClass,
} from '../components/ui.tsx';

/**
 * The overview answers one question: is anything on fire right now, and did
 * the notification actually reach anyone. Numbers stay in mono so they do not
 * jump while the page polls.
 */
export function OverviewPage() {
  const stats = useQuery({
    queryKey: ['stats', 'overview'],
    queryFn: () => api.stats.overview(),
    refetchInterval: 15_000,
  });

  const firing = useQuery({
    queryKey: ['alerts', { status: 'firing', limit: 6 }],
    queryFn: () => api.alerts.list({ status: 'firing', limit: 6 }),
    refetchInterval: 15_000,
  });

  return (
    <>
      <PageHeader
        title="Übersicht"
        description="Was gerade offen ist, und ob die Benachrichtigungen dazu angekommen sind."
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Offen"
          value={stats.data?.alerts_firing}
          loading={stats.isLoading}
          tone={stats.data && stats.data.alerts_firing > 0 ? 'critical' : 'neutral'}
          note={
            stats.data
              ? `${stats.data.alerts_acknowledged} quittiert`
              : undefined
          }
        />
        <StatTile
          label="Letzte 24 Stunden"
          value={stats.data?.alerts_last_24h}
          loading={stats.isLoading}
          note={stats.data ? `${stats.data.active_sources} aktive Quellen` : undefined}
        />
        <StatTile
          label="Zustellungen offen"
          value={stats.data?.deliveries_pending}
          loading={stats.isLoading}
          tone={stats.data && stats.data.deliveries_failed_24h > 0 ? 'warning' : 'neutral'}
          note={
            stats.data
              ? `${stats.data.deliveries_failed_24h} aufgegeben in 24 h`
              : undefined
          }
        />
        <StatTile
          label="Zustellquote 24 h"
          value={stats.data ? percent(stats.data.delivery_success_rate_24h) : undefined}
          loading={stats.isLoading}
          note={stats.data ? `${stats.data.active_channels} aktive Kanäle` : undefined}
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        <Panel title="Alarme pro Stunde">
          {stats.isLoading ? (
            <Skeleton className="h-28 w-full" />
          ) : stats.data ? (
            <AlertTimeline buckets={stats.data.timeline} />
          ) : (
            <ErrorState message="Die Statistik konnte nicht geladen werden." onRetry={() => stats.refetch()} />
          )}
        </Panel>

        <Panel title="Offen nach Schweregrad">
          {stats.isLoading ? (
            <div className="space-y-3">
              {SEVERITIES.map((s) => (
                <Skeleton key={s} className="h-5 w-full" />
              ))}
            </div>
          ) : (
            <ul className="space-y-2.5">
              {[...SEVERITIES].reverse().map((severity) => {
                const count = stats.data?.by_severity.find((s) => s.severity === severity)?.count ?? 0;
                const max = Math.max(1, ...(stats.data?.by_severity.map((s) => s.count) ?? [1]));
                return (
                  <li key={severity} className="flex items-center gap-3">
                    <span className="w-20 shrink-0 text-[12px] text-ink-2">
                      {SEVERITY_LABEL[severity]}
                    </span>
                    <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-sunken">
                      <span
                        className={cx('block h-full rounded-full', severityRuleClass(severity))}
                        style={{ width: `${count === 0 ? 0 : Math.max(4, (count / max) * 100)}%` }}
                      />
                    </span>
                    <span className="tnum w-8 shrink-0 text-right text-[13px] text-ink">{count}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>
      </div>

      <MonitorsPanel />

      <Panel
        className="mt-4"
        title="Offene Alarme"
        bodyClassName="p-0"
        action={
          <Link
            to="/alarme"
            className="inline-flex items-center gap-1 text-[12px] text-ink-2 transition-colors hover:text-ink"
          >
            Alle ansehen
            <ArrowRightIcon size={13} />
          </Link>
        }
      >
        {firing.isLoading ? (
          <ListSkeleton rows={4} />
        ) : firing.isError ? (
          <ErrorState message="Die Alarmliste konnte nicht geladen werden." onRetry={() => firing.refetch()} />
        ) : firing.data && firing.data.items.length > 0 ? (
          <ul className="divide-y divide-[var(--border-subtle)]">
            {firing.data.items.map((alert) => (
              <li key={alert.id} className="enter">
                <Link
                  to={`/alarme/${alert.id}`}
                  className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-hover"
                >
                  <span
                    aria-hidden
                    className={cx('h-8 w-[3px] shrink-0 rounded-full', severityRuleClass(alert.severity))}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-ink">
                      {alert.title}
                    </span>
                    <span className="mt-0.5 block truncate text-[12px] text-ink-3">
                      {alert.source_name}
                      {alert.occurrences > 1 ? ` - ${alert.occurrences}x` : ''}
                    </span>
                  </span>
                  <SeverityChip severity={alert.severity} />
                  <span className="hidden w-24 shrink-0 text-right text-[12px] text-ink-3 sm:block">
                    {relativeTime(alert.last_seen_at)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<CheckCircleIcon size={26} weight="light" />}
            title="Nichts offen"
            description="Kein Alarm wartet gerade auf eine Reaktion. Neue Alarme erscheinen hier, sobald eine Quelle sie meldet."
          />
        )}
      </Panel>
    </>
  );
}

/**
 * Alle Monitore auf einen Blick: links die Summe als Balken, rechts jeder
 * Monitor als Streifen mit denselben Zeitgrenzen. Ein roter Balken links
 * lässt sich so rechts dem Monitor zuordnen, ohne die Seite zu wechseln.
 */
function MonitorsPanel() {
  const [range, setRange] = useState<MonitorHistoryRange>('24h');
  const overview = useQuery({
    queryKey: ['stats', 'monitors', range, viewerTimeZone],
    queryFn: () => api.stats.monitors({ range, tz: viewerTimeZone }),
    refetchInterval: 30_000,
  });
  const data = overview.data;

  return (
    <Panel
      className="mt-4"
      title="Monitore"
      action={
        data && data.monitors.length > 0 ? (
          <div className="hidden sm:block">
            <HistoryRangeTabs value={range} onChange={setRange} />
          </div>
        ) : null
      }
    >
      {overview.isLoading ? (
        <Skeleton className="h-44 w-full" />
      ) : overview.isError || !data ? (
        <ErrorState message="Der Monitor-Verlauf konnte nicht geladen werden." onRetry={() => overview.refetch()} />
      ) : data.monitors.length === 0 ? (
        <EmptyState
          icon={<PulseIcon size={26} weight="light" />}
          title="Noch kein Monitor"
          description="Ein Monitor prüft von hier aus Webseiten, Ports, Mailserver, Zertifikate oder DNS. Sein Verlauf erscheint dann an dieser Stelle."
          action={
            <Link
              to="/quellen"
              state={{ tab: 'monitors' }}
              className="inline-flex items-center gap-1 text-[13px] font-medium text-accent hover:text-accent-hover"
            >
              Zu den Monitoren
              <ArrowRightIcon size={13} />
            </Link>
          }
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1.35fr_1fr]">
          <div className="min-w-0">
            {/* Neben dem Titel ist auf dem Telefon kein Platz für vier Tabs. */}
            <div className="mb-4 sm:hidden">
              <HistoryRangeTabs value={range} onChange={setRange} />
            </div>
            <p className="mb-3 text-[12px] text-ink-2">
              <span className="tnum font-medium text-ink">{uptimePercent(data.totals.uptime)}</span> erreichbar,{' '}
              <span className="tnum">{data.totals.checks}</span> Prüfungen,{' '}
              <span className={cx('tnum', data.totals.failures > 0 && 'text-critical')}>{data.totals.failures}</span>{' '}
              fehlgeschlagen
            </p>
            <MonitorHistoryChart
              buckets={data.buckets}
              bucket={data.bucket}
              startLabel={historyStartLabel(range, data.from)}
              caption={`Prüfungen aller Monitore, ${HISTORY_RANGE_LABEL[range]}`}
            />
          </div>

          <div className="min-w-0">
            <ul className="-mx-2 divide-y divide-[var(--border-subtle)]">
              {data.monitors.map((m) => (
                <li key={m.id}>
                  <Link
                    to={`/quellen/monitore/${m.id}`}
                    className={cx(
                      'grid grid-cols-[minmax(0,1fr)_5.5rem_3.75rem] items-center gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,9rem)_4.5rem] rounded-md px-2 py-2.5 transition-colors hover:bg-hover',
                      !m.is_active && 'opacity-55',
                    )}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        aria-hidden
                        className={cx(
                          'size-1.5 shrink-0 rounded-full',
                          !m.is_active ? 'bg-ink-3' : m.status === 'down' ? 'bg-critical' : m.status === 'up' ? 'bg-accent' : 'bg-ink-3',
                        )}
                      />
                      <span className="truncate text-[13px] font-medium text-ink">{m.name}</span>
                      {!m.is_active ? <span className="shrink-0 text-[11px] text-ink-3">pausiert</span> : null}
                      {m.is_active && m.status === 'down' ? (
                        <span className="shrink-0 text-[11px] font-medium text-critical">Ausfall</span>
                      ) : null}
                    </span>
                    <UptimeStrip
                      buckets={m.buckets}
                      bucket={data.bucket}
                      label={`${m.name}: ${uptimePercent(m.totals.uptime)} erreichbar`}
                    />
                    <span className="tnum text-right text-[12px] text-ink-2">{uptimePercent(m.totals.uptime)}</span>
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-3 border-t border-line pt-3">
              <StripLegend />
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}
